import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type ApiError, type ListCadencesResult } from '@whinerp/client';
import { Button, Panel, SectionLabel } from '@whinerp/ui';
import { useErrorMessage, useT } from '../../app/i18n.js';
import { useEntitlement } from '../../app/session.js';

/** A contact's cadences (F-055): where they are, enrol them, or record that they replied - which stops every cadence. */
export function ContactCadencePanel({ contactId }: { contactId: string }): JSX.Element | null {
  const t = useT();
  const errorText = useErrorMessage();
  const queryClient = useQueryClient();
  const enabled = useEntitlement('F-055');
  const [cadenceId, setCadenceId] = useState('');
  const list = useQuery<ListCadencesResult, ApiError>({ queryKey: ['cadences'], queryFn: () => api.listCadences({}), enabled: enabled.enabled });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['cadences'] });
  const enrol = useMutation({ mutationFn: () => api.enrolInCadence({ path: { contactId }, body: { cadenceId } }), onSuccess: refresh });
  const replied = useMutation({ mutationFn: () => api.logContactReply({ path: { contactId }, body: { note: null } }), onSuccess: refresh });
  if (!enabled.enabled || !list.data) return null;
  const mine = list.data.cadences.flatMap((cadence) => cadence.enrolled.filter((e) => e.contactId === contactId).map((e) => ({ ...e, cadence: cadence.name })));
  const open = list.data.cadences.filter((cadence) => cadence.active);
  return (
    <Panel data-region="contact-cadences">
      <div className="flex flex-col gap-2">
        <SectionLabel>{t('cadence.title')}</SectionLabel>
        {mine.map((entry) => (
          <p key={entry.enrolmentId} data-contact-cadence={entry.cadence} className="text-dense text-ink">
            {entry.cadence} · {entry.status === 'active' ? t('cadence.stepShort', { values: { step: String(entry.currentStep) } }) : t(`cadence.status.${entry.status as 'completed'}`)}
            {entry.exitReason ? ` - ${entry.exitReason}` : ''}
          </p>
        ))}
        <div className="flex flex-wrap items-center gap-2">
          {open.length > 0 ? (
            <>
              <select data-contact-cadence-select aria-label={t('cadence.title')} className="h-input rounded-control border border-control bg-surface px-3 text-body text-ink" value={cadenceId} onChange={(e) => setCadenceId(e.target.value)}>
                <option value="">{t('cadence.choose')}</option>
                {open.map((cadence) => (
                  <option key={cadence.cadenceId} value={cadence.cadenceId}>{cadence.name}</option>
                ))}
              </select>
              <Button intent="secondary" size="small" data-contact-cadence-enrol disabled={!cadenceId || enrol.isPending} onClick={() => enrol.mutate()}>
                {t('cadence.enrol')}
              </Button>
            </>
          ) : null}
          {mine.some((entry) => entry.status === 'active') ? (
            <Button intent="ghost" size="small" data-contact-replied disabled={replied.isPending} onClick={() => replied.mutate()}>
              {t('cadence.theyReplied')}
            </Button>
          ) : null}
        </div>
        {enrol.error ?? replied.error ? <p className="text-dense text-danger-text">{errorText((enrol.error ?? replied.error)!)}</p> : null}
      </div>
    </Panel>
  );
}
